/* Price Tracker page controller (/pages/price-tracker).
   Two client-side views on one Shopify page, routed via the ?product= query param
   with pushState/popstate so product pages feel like real pages:
     market view  — stats, 7-day movers, browse-by-set strip, search/filters/sort, grid
     product view — hero chart (QuazaPriceChart widget), stat tiles, and, when the
                    product exists in the store, details from /products/<handle>.js
                    (price, stock, description) with a Buy CTA.
   Data: catalog + per-product history from the Quaza prices Worker (shared with the
   iOS app); store details from Shopify's public product JSON (same-origin). */
(function () {
  'use strict';
  var root = document.querySelector('[data-qpt]');
  if (!root || !window.QuazaPriceChart) return;

  var API = root.getAttribute('data-api');
  var C = window.QuazaPriceChart;
  var marketEl = root.querySelector('[data-qpt-market]');
  var productEl = root.querySelector('[data-qpt-product]');
  var idxMainEl = root.querySelector('[data-qpt-idxmain]');
  var idxListEl = root.querySelector('[data-qpt-idxlist]');
  var idxCardsEl = root.querySelector('[data-qpt-idxcards]');
  var gainersEl = root.querySelector('[data-qpt-gainers]');
  var losersEl = root.querySelector('[data-qpt-losers]');
  var catalogEl = root.querySelector('[data-qpt-catalog]');
  var setsEl = root.querySelector('[data-qpt-sets]');
  var erasEl = root.querySelector('[data-qpt-eras]');
  var searchEl = root.querySelector('[data-qpt-search]');
  var filtersEl = root.querySelector('[data-qpt-filters]');
  var sortEl = root.querySelector('[data-qpt-sort]');
  var countEl = root.querySelector('[data-qpt-count]');
  var updatedEl = root.querySelector('[data-qpt-updated]');

  var products = [];
  var byId = {};
  var typeCardsEl = root.querySelector('[data-qpt-typecards]');
  var typechipEl = root.querySelector('[data-qpt-typechip]');
  var mktEl = root.querySelector('[data-qpt-mkt]');
  var clearEl = root.querySelector('[data-qpt-clear]');

  var lang = 'all';
  var query = '';
  var sort = 'featured';
  var mode = 'sealed'; // 'sealed' | 'singles' — completely separate market segments
  var activeEra = null; // browse era label (see eraOf) or null for all
  var activeSet = null; // set_name or null for all
  var activeType = null; // sealed product type key (see PTYPES) or null for all
  var lastIdx = null; // latest /indexes payload for the default 3m range
  var idxPayloadCache = {}; // range key -> /indexes payload (index-page range pills)

  function inMode(p) {
    return mode === 'singles' ? p.type === 'single' : p.type !== 'single';
  }

  /* ---------- taxonomy: eras, release order, product types ----------
     The catalog carries language ('EN'/'JP') and set_release (ISO date) per
     product, so organization is data-driven: non-EN sets browse under an
     "International" era, and everything lists newest release first. */

  function eraOf(p) {
    if (p.language && p.language !== 'EN') return 'International';
    var e = p.era || 'Other';
    return e === 'Other' ? 'Special Sets' : e;
  }
  var ERA_LABELS = { 'Mega': 'Mega Evolution' };
  function eraLabel(era) { return ERA_LABELS[era] || era; }

  /* order matters: 'Ultra-Premium Collection' must classify before 'Collection' */
  var PTYPES = [
    { key: 'etb', label: 'Elite Trainer Boxes', one: 'Elite Trainer Box', re: /elite trainer/i },
    { key: 'upc', label: 'Ultra-Premium Collections', one: 'Ultra-Premium Collection', re: /ultra.?premium/i },
    { key: 'booster-box', label: 'Booster Boxes', one: 'Booster Box', re: /booster (box|display)/i },
    { key: 'booster-bundle', label: 'Booster Bundles', one: 'Booster Bundle', re: /booster bundle/i },
    { key: 'collection', label: 'Collection Boxes', one: 'Collection Box', re: /collection/i }
  ];
  function ptypeOf(p) {
    if (p.type === 'single') return null;
    for (var i = 0; i < PTYPES.length; i++) if (PTYPES[i].re.test(p.name)) return PTYPES[i].key;
    return 'other';
  }
  function ptypeDef(key) {
    return PTYPES.filter(function (t) { return t.key === key; })[0] || null;
  }

  var SET_INFO = {}; // set_name -> { rel, era, lang, sealed, singles }
  var ERA_RANK = {}; // browse era label -> display rank (0 = first)
  function buildTaxonomy() {
    SET_INFO = {};
    var maxRel = {};
    products.forEach(function (p) {
      var s = SET_INFO[p.set_name] ||
        (SET_INFO[p.set_name] = { rel: '', era: p.era || 'Other', lang: p.language || 'EN', sealed: 0, singles: 0 });
      if (p.set_release && p.set_release > s.rel) s.rel = p.set_release;
      if (p.type === 'single') s.singles++; else s.sealed++;
      var l = eraOf(p);
      if (!(l in maxRel) || (p.set_release || '') > maxRel[l]) maxRel[l] = p.set_release || '';
    });
    // eras newest-first; cross-era Special Sets after the real eras, International last
    function bucket(l) { return l === 'International' ? 2 : l === 'Special Sets' ? 1 : 0; }
    ERA_RANK = {};
    Object.keys(maxRel).sort(function (a, b) {
      if (bucket(a) !== bucket(b)) return bucket(a) - bucket(b);
      return maxRel[a] < maxRel[b] ? 1 : -1;
    }).forEach(function (l, i) { ERA_RANK[l] = i; });
  }
  function setRel(name) { return (SET_INFO[name] && SET_INFO[name].rel) || ''; }
  /* sets for the current mode (and optionally era), newest release first */
  function setsFor(era) {
    var seen = {}, out = [];
    products.forEach(function (p) {
      if (!inMode(p) || seen[p.set_name]) return;
      if (era && eraOf(p) !== era) return;
      seen[p.set_name] = 1;
      out.push(p.set_name);
    });
    return out.sort(function (a, b) { return setRel(a) < setRel(b) ? 1 : -1; });
  }
  var baseTitle = document.title;
  var updatedAtText = '';

  /* ---------- helpers ---------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function chip(pct) {
    var c = el('span', 'qpt-chip');
    if (pct == null) { c.className += ' qpt-chip--flat'; c.textContent = '—'; }
    else if (pct >= 0) { c.className += ' qpt-chip--up'; c.textContent = '▲ ' + pct + '%'; }
    else { c.className += ' qpt-chip--down'; c.textContent = '▼ ' + Math.abs(pct) + '%'; }
    return c;
  }

  function img(src, alt, size) {
    var i = document.createElement('img');
    if (src) i.src = src;
    i.alt = alt || '';
    i.loading = 'lazy';
    i.decoding = 'async';
    i.width = size; i.height = size;
    return i;
  }

  /* Official set logos, hosted on the store's Files CDN. The map arrives in
     /catalog as set_logos (set_name → full URL), so this file keeps no slug
     list of its own: one source of truth, shared with the iOS app, and adding
     a logo is a Worker deploy. It stays an allowlist — a set that is absent
     has no logo and is never probed for one — and an empty map (stale cache,
     rolled-back Worker) simply renders every set text-only. */
  var logoMap = {};

  /* Shopify Files URLs accept ?height=, so ask the CDN for a resized copy
     rather than pulling a 900px wordmark down to paint it at 20px. Heights are
     quantised onto a ladder instead of passed straight through: one logo then
     shares a single URL across the nav chip (18), set strip (20), index row
     (20) and set card (26), so it is fetched once rather than four times. */
  var LOGO_LADDER = [52, 88]; // 2x the tallest of each group: 26px card, 44px index hero
  function logoSrc(url, height) {
    var want = (height || 22) * 2;
    var h = LOGO_LADDER[LOGO_LADDER.length - 1];
    for (var i = 0; i < LOGO_LADDER.length; i++) {
      if (LOGO_LADDER[i] >= want) { h = LOGO_LADDER[i]; break; }
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'height=' + h;
  }
  function hasSetLogo(setName) {
    return !!logoMap[setName];
  }
  function setLogo(setName, height) {
    var url = logoMap[setName];
    if (!url) return null;
    var i = document.createElement('img');
    i.src = logoSrc(url, height);
    i.alt = '';
    i.className = 'qpt-setlogo';
    i.loading = 'lazy';
    i.decoding = 'async';
    i.style.height = (height || 22) + 'px';
    i.onerror = function () { if (i.parentNode) i.parentNode.removeChild(i); };
    return i;
  }

  function metaLine(p) {
    return [p.set_name, p.era, p.language === 'JP' ? 'Japanese' : null, p.grade]
      .filter(Boolean).join(' · ');
  }

  function matches(p) {
    if (!inMode(p)) return false;
    if (activeEra && eraOf(p) !== activeEra) return false;
    if (activeSet && p.set_name !== activeSet) return false;
    if (activeType && ptypeOf(p) !== activeType) return false;
    if (lang !== 'all' && p.language !== lang) return false;
    if (!query) return true;
    return (p.name + ' ' + (p.set_name || '') + ' ' + (p.era || '')).toLowerCase().indexOf(query) !== -1;
  }

  /* market URL state (?market=&era=&set=&ptype=) — shareable */
  function syncMarketUrl() {
    var u = new URL(location.href);
    if (mode === 'singles') u.searchParams.set('market', 'singles'); else u.searchParams.delete('market');
    if (activeEra) u.searchParams.set('era', activeEra); else u.searchParams.delete('era');
    if (activeSet) u.searchParams.set('set', activeSet); else u.searchParams.delete('set');
    if (activeType) u.searchParams.set('ptype', activeType); else u.searchParams.delete('ptype');
    history.replaceState(history.state, '', u);
  }

  function applyEraSet(era, set, scroll) {
    activeEra = era || null;
    activeSet = set || null;
    if (activeSet && !activeEra) {
      var owner = products.filter(function (p) { return p.set_name === activeSet; })[0];
      if (owner) activeEra = eraOf(owner);
    }
    renderEraTabs();
    renderSetStrip();
    renderCatalog();
    syncMarketUrl();
    navSync();
    if (scroll && erasEl) erasEl.scrollIntoView({ block: 'start' });
  }

  /* product-type filter (Products menu): a clean cross-set listing */
  function applyType(t, scroll) {
    activeType = t || null;
    activeEra = null;
    activeSet = null;
    renderEraTabs();
    renderSetStrip();
    renderCatalog();
    syncMarketUrl();
    navSync();
    if (scroll && erasEl) erasEl.scrollIntoView({ block: 'start' });
  }

  /* Sealed ↔ Singles: two separate markets, switched from the mega nav.
     Indexes are sealed-only, so every index section hides in singles mode. */
  function setMode(next) {
    if (mode === next) return;
    mode = next;
    activeEra = null;
    activeSet = null;
    activeType = null;
    var sealedOnly = [idxMainEl, idxListEl, typeCardsEl, idxCardsEl];
    sealedOnly.forEach(function (m) {
      if (m) { var sec = m.closest('section'); (sec || m).hidden = (mode === 'singles'); }
    });
    renderStats();
    renderLists();
    renderEraTabs();
    renderSetStrip();
    renderCatalog();
    syncMarketUrl();
    navSync();
  }

  /* ---------- sticky mega nav: Sets · Products · Indices · Singles · International ----------
     Panels are built lazily from catalog data on each open, so they are always
     in sync with products/lastIdx and cost nothing until a tab is tapped. */

  var megaNavEl = root.querySelector('[data-qpt-meganav]');
  var panelEl = root.querySelector('[data-qpt-panel]');
  var openTab = null;

  /* light/dark theme for the tracker only — palette swaps via CSS vars on .qpt;
     the pre-paint inline script in the section already applied the saved choice */
  var themeBtn = root.querySelector('[data-qpt-theme]');
  function applyTheme(t) {
    if (t === 'dark') root.setAttribute('data-theme', 'dark');
    else root.removeAttribute('data-theme');
    if (themeBtn) themeBtn.setAttribute('aria-label', t === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
    try { localStorage.setItem('qpt-theme', t); } catch (e) { /* private browsing */ }
  }
  if (themeBtn) {
    if (root.getAttribute('data-theme') === 'dark') themeBtn.setAttribute('aria-label', 'Switch to light mode');
    themeBtn.addEventListener('click', function () {
      applyTheme(root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark');
    });
  }

  // dock right below the theme header. Impulse makes #SiteHeader ITSELF
  // position:fixed once scrolled (not its wrapper), and slides it in/out —
  // so track the header's live bottom edge on every scroll, not a one-off height.
  var siteHeader = document.querySelector('.site-header');
  var lastStickyTop = null;
  function setStickyTop() {
    var h = 0;
    if (siteHeader) {
      var own = getComputedStyle(siteHeader).position;
      var par = getComputedStyle(siteHeader.parentElement || siteHeader).position;
      if (own === 'fixed' || own === 'sticky' || par === 'fixed' || par === 'sticky') {
        h = Math.max(0, Math.round(siteHeader.getBoundingClientRect().bottom));
      }
    }
    if (h !== lastStickyTop) {
      lastStickyTop = h;
      root.style.setProperty('--qpt-sticky-top', h + 'px');
    }
  }
  setStickyTop();
  window.addEventListener('resize', setStickyTop, { passive: true });
  var stickyRaf = 0;
  window.addEventListener('scroll', function () {
    if (stickyRaf) return;
    stickyRaf = requestAnimationFrame(function () { stickyRaf = 0; setStickyTop(); });
  }, { passive: true });
  // the theme flips the header to fixed AFTER our scroll frame runs — re-measure
  // when its class/style changes, and once more after its slide-in transition
  if (siteHeader && window.MutationObserver) {
    var settleTimer = 0;
    new MutationObserver(function () {
      setStickyTop();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(setStickyTop, 360);
    }).observe(siteHeader, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  /* the Singles tab reflects the market mode; International reflects its era filter */
  function navSync() {
    if (megaNavEl) {
      megaNavEl.querySelectorAll('.qpt-mntab').forEach(function (b) {
        var key = b.getAttribute('data-tab');
        var on = (key === 'singles' && mode === 'singles') ||
                 (key === 'intl' && activeEra === 'International');
        if (on) b.setAttribute('data-active', ''); else b.removeAttribute('data-active');
      });
    }
    // the toolbar's Sealed | Singles toggle mirrors the same mode state
    if (mktEl) {
      mktEl.querySelectorAll('[data-mkt]').forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-mkt') === mode ? 'true' : 'false');
      });
    }
  }

  function closePanel() {
    if (!panelEl) return;
    panelEl.hidden = true;
    panelEl.textContent = '';
    openTab = null;
    megaNavEl.querySelectorAll('.qpt-mntab').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
  }

  function panelGroup(title) {
    var g = el('div', 'qpt-panelgroup');
    if (title) g.appendChild(el('h4', 'qpt-panelgroup-h', title));
    return g;
  }
  function panelLink(label, count, nav, arg, extra) {
    var a = document.createElement('a');
    a.className = 'qpt-panellink';
    a.href = '?' + (nav === 'index' ? 'index=' + encodeURIComponent(arg)
      : nav === 'ptype' ? 'ptype=' + encodeURIComponent(arg)
      : nav === 'era' ? 'era=' + encodeURIComponent(arg)
      : nav === 'set' ? 'set=' + encodeURIComponent(arg) : '');
    a.setAttribute('data-mnav', nav);
    if (arg != null) a.setAttribute('data-arg', arg);
    if (extra && extra.market) { a.setAttribute('data-market', extra.market); if (extra.market === 'singles') a.href = '?market=singles&set=' + encodeURIComponent(arg); }
    if (extra && extra.logo) { var lg = setLogo(arg, 18); if (lg) a.appendChild(lg); }
    a.appendChild(el('span', 'qpt-panellink-t', label));
    if (count != null) a.appendChild(el('span', 'qpt-panellink-n', String(count)));
    return a;
  }
  function panelFoot(label, nav, arg) {
    var a = document.createElement('a');
    a.className = 'qpt-seeall qpt-panelfoot-a';
    a.href = '#' + (nav === 'anchor' ? arg : '');
    a.setAttribute('data-mnav', nav);
    if (arg != null) a.setAttribute('data-arg', arg);
    a.textContent = label;
    return a;
  }
  function setCount(name) {
    var s = SET_INFO[name] || {};
    return mode === 'singles' ? s.singles : s.sealed;
  }

  var PANEL_BUILDERS = {
    /* English sealed sets, era-grouped, newest first */
    sets: function (frame) {
      var eras = [];
      products.forEach(function (p) {
        var l = eraOf(p);
        if (p.type === 'single' || l === 'International' || eras.indexOf(l) !== -1) return;
        eras.push(l);
      });
      eras.sort(function (a, b) { return (ERA_RANK[a] || 0) - (ERA_RANK[b] || 0); });
      eras.forEach(function (era) {
        var g = panelGroup(eraLabel(era));
        setsFor(era).forEach(function (name) {
          if ((SET_INFO[name] || {}).lang !== 'EN') return;
          g.appendChild(panelLink(name, setCount(name), 'set', name, { logo: 1 }));
        });
        frame.appendChild(g);
      });
      frame.appendChild(panelFoot('Browse all sealed products →', 'browse-all', ''));
    },

    /* sealed product types, plus movers */
    products: function (frame) {
      var counts = {};
      products.forEach(function (p) { var k = ptypeOf(p); if (k) counts[k] = (counts[k] || 0) + 1; });
      var g = panelGroup('Shop by product type');
      // alphabetical for display — PTYPES itself stays in classification
      // priority order (UPC must match before Collection)
      PTYPES.slice().sort(function (a, b) { return a.label.localeCompare(b.label); }).forEach(function (t) {
        if (counts[t.key]) g.appendChild(panelLink(t.label, counts[t.key], 'ptype', t.key));
      });
      g.appendChild(panelLink('All sealed products', products.filter(function (p) { return p.type !== 'single'; }).length, 'ptype', ''));
      frame.appendChild(g);
      frame.appendChild(panelFoot('Top movers →', 'anchor', 'qpt-movers'));
    },

    /* every index gets a page — flagship, product types, then sets newest first */
    indices: function (frame) {
      var g1 = panelGroup('Market index');
      g1.appendChild(panelLink('Quaza Index', null, 'index', 'quaza'));
      frame.appendChild(g1);
      var g2 = panelGroup('Product indexes');
      [['booster-box', 'Booster Box Index'], ['booster-bundle', 'Booster Bundle Index'],
       ['collection', 'Collection Box Index'], ['etb', 'Elite Trainer Box Index'],
       ['upc', 'Ultra-Premium Collection Index']]
        .forEach(function (t) { g2.appendChild(panelLink(t[1], null, 'index', t[0])); });
      frame.appendChild(g2);
      var g3 = panelGroup('Set indexes');
      var names = lastIdx && lastIdx.sets
        ? lastIdx.sets.map(function (s) { return s.set_name; })
        : setsFor(null).filter(function (n) { return (SET_INFO[n] || {}).sealed > 0; });
      // English sets only (owner call) — JP sets stay reachable via International
      names = names.filter(function (n) { return (SET_INFO[n] || {}).lang === 'EN'; });
      names.sort(function (a, b) {
        // newest first; the misc catch-all bucket always sinks to the bottom
        var ma = a === MISC_SET ? 1 : 0, mb = b === MISC_SET ? 1 : 0;
        if (ma !== mb) return ma - mb;
        return setRel(a) < setRel(b) ? 1 : -1;
      });
      names.forEach(function (name) {
        /* Deliberately text-only: the owner does not want set logos in the
           Indices panel (decided 2026-07-30). It never rendered one anyway, as
           the nav arg is prefixed with "set:" and so never matches a set_logos
           key. The flag is removed rather than left inert, so that correcting
           the key later cannot silently switch 40 logos on. */
        g3.appendChild(panelLink(name, null, 'index', 'set:' + name));
      });
      frame.appendChild(g3);
    },

    /* switches the whole page to the singles market */
    singles: function (frame) {
      var g0 = panelGroup(null);
      var all = products.filter(function (p) { return p.type === 'single'; }).length;
      g0.appendChild(panelLink('All single cards', all, 'singles-all', ''));
      frame.appendChild(g0);
      var eras = [];
      products.forEach(function (p) {
        var l = eraOf(p);
        if (p.type !== 'single' || l === 'International' || eras.indexOf(l) !== -1) return;
        eras.push(l);
      });
      eras.sort(function (a, b) { return (ERA_RANK[a] || 0) - (ERA_RANK[b] || 0); });
      eras.forEach(function (era) {
        var g = panelGroup(eraLabel(era));
        var seen = {};
        products.forEach(function (p) {
          if (p.type !== 'single' || eraOf(p) !== era || seen[p.set_name]) return;
          seen[p.set_name] = 1;
        });
        Object.keys(seen).sort(function (a, b) { return setRel(a) < setRel(b) ? 1 : -1; })
          .forEach(function (name) {
            g.appendChild(panelLink(name, (SET_INFO[name] || {}).singles, 'set', name, { logo: 1, market: 'singles' }));
          });
        frame.appendChild(g);
      });
    },

    /* Japanese (and future non-EN) sets — sealed and singles kept apart */
    intl: function (frame) {
      var sealed = {}, singles = {};
      products.forEach(function (p) {
        if (eraOf(p) !== 'International') return;
        (p.type === 'single' ? singles : sealed)[p.set_name] = 1;
      });
      function fill(title, names, market) {
        if (!names.length) return;
        var g = panelGroup(title);
        names.sort(function (a, b) { return setRel(a) < setRel(b) ? 1 : -1; })
          .forEach(function (name) {
            var info = SET_INFO[name] || {};
            g.appendChild(panelLink(name, market === 'singles' ? info.singles : info.sealed, 'set', name,
              market === 'singles' ? { market: 'singles' } : { logo: 1 }));
          });
        frame.appendChild(g);
      }
      fill('Japanese · Sealed', Object.keys(sealed), 'sealed');
      fill('Japanese · Singles', Object.keys(singles), 'singles');
      frame.appendChild(panelFoot('All international products →', 'era', 'International'));
    }
  };

  function openPanel(key) {
    if (!panelEl) return;
    panelEl.textContent = '';
    var frame = el('div', 'page-width qpt-panel-in');
    if (!products.length) {
      frame.appendChild(el('div', 'qpt-status', 'Loading market data…'));
    } else {
      PANEL_BUILDERS[key](frame);
    }
    panelEl.appendChild(frame);
    panelEl.hidden = false;
    openTab = key;
    megaNavEl.querySelectorAll('.qpt-mntab').forEach(function (b) {
      b.setAttribute('aria-expanded', b.getAttribute('data-tab') === key ? 'true' : 'false');
    });
  }

  function handlePanelLink(a) {
    var nav = a.getAttribute('data-mnav');
    var arg = a.getAttribute('data-arg') || '';
    var market = a.getAttribute('data-market');
    closePanel();
    if (nav === 'index') { navigateToIndex(arg); return; }
    if (root.getAttribute('data-view') !== 'market') {
      // filters/anchors live on the market view — push a market entry first
      var u = new URL(location.href);
      u.searchParams.delete('product');
      u.searchParams.delete('index');
      history.pushState({}, '', u);
      showMarket();
    }
    if (nav === 'set') {
      if (market === 'singles' && mode !== 'singles') setMode('singles');
      if (market !== 'singles' && mode !== 'sealed') setMode('sealed');
      applyEraSet(null, arg, true);
    } else if (nav === 'era') {
      applyEraSet(arg, null, true);
    } else if (nav === 'ptype') {
      applyType(arg || null, true);
    } else if (nav === 'singles-all') {
      if (mode !== 'singles') setMode('singles');
      applyEraSet(null, null, true);
    } else if (nav === 'browse-all') {
      applyType(null, true);
    } else if (nav === 'anchor') {
      var t = document.getElementById(arg);
      if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (megaNavEl) {
    megaNavEl.addEventListener('click', function (ev) {
      var tab = ev.target.closest('[data-tab]');
      if (tab) {
        var key = tab.getAttribute('data-tab');
        if (openTab === key) { closePanel(); return; }
        // per owner: Singles switches the whole page; sealed tabs switch it back
        if (key === 'singles' && mode !== 'singles') setMode('singles');
        if ((key === 'sets' || key === 'products' || key === 'indices') && mode !== 'sealed') setMode('sealed');
        openPanel(key);
        return;
      }
      var a = ev.target.closest('a[data-mnav]');
      if (a) { ev.preventDefault(); handlePanelLink(a); }
    });
    document.addEventListener('click', function (ev) {
      if (openTab && !megaNavEl.contains(ev.target)) closePanel();
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && openTab) closePanel();
    });
  }

  /* draw mini charts only when their card scrolls into view (perf) */
  var lazyChartIO = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          lazyChartIO.unobserve(e.target);
          var fn = e.target.__qptDraw;
          if (fn) { e.target.__qptDraw = null; fn(); }
        });
      }, { rootMargin: '200px 0px' })
    : null;
  function lazyChart(mount, draw) {
    if (lazyChartIO) { mount.__qptDraw = draw; lazyChartIO.observe(mount); }
    else requestAnimationFrame(draw);
  }

  /* ---------- market: overview ticker (TradingView-style scrolling tape) ----------
     One continuous line of Pokémon market stats instead of wrapping tiles.
     Chips use data-open / data-goidx so the delegated root click handler works
     on the cloned half of the loop too (cloneNode drops direct listeners). */

  function groupLevel(g) {
    if (!g) return null;
    if (g.level != null) return g.level;
    var s = g.series || [];
    return s.length ? s[s.length - 1].v : null;
  }
  function groupPct(g) {
    if (!g) return null;
    if (g.change_pct != null) return g.change_pct;
    var s = g.series || [];
    if (s.length < 2 || !s[0].v) return null;
    return Math.round((s[s.length - 1].v / s[0].v - 1) * 1000) / 10;
  }

  function renderStats() {
    var statsEl = root.querySelector('[data-qpt-stats]');
    if (!statsEl) return;
    var moved = products.filter(function (p) {
      return inMode(p) && p.change7d_pct != null && p.price_cents != null;
    });
    if (!moved.length) { statsEl.hidden = true; return; }
    statsEl.hidden = false;

    var sum = 0, up = 0, down = 0;
    moved.forEach(function (p) {
      sum += p.change7d_pct;
      if (p.change7d_pct > 0) up++;
      else if (p.change7d_pct < 0) down++;
    });
    var avg = Math.round((sum / moved.length) * 10) / 10;
    var sorted = moved.slice().sort(function (a, b) { return b.change7d_pct - a.change7d_pct; });

    function item(opts) {
      var t = el(opts.open || opts.goidx ? 'button' : 'span', 'qpt-tickitem');
      if (t.tagName === 'BUTTON') {
        t.type = 'button';
        if (opts.open) t.setAttribute('data-open', opts.open);
        if (opts.goidx) t.setAttribute('data-goidx', opts.goidx);
      }
      if (opts.label) t.appendChild(el('span', 'qpt-tickitem-l', opts.label));
      if (opts.name) t.appendChild(el('span', 'qpt-tickitem-n', opts.name));
      if (opts.value != null) t.appendChild(el('span', 'qpt-tickitem-v', opts.value));
      if (opts.chg !== undefined) t.appendChild(chgText(opts.chg));
      if (opts.count) {
        t.appendChild(el('span', 'qpt-chgtext ' + opts.count.dir,
          opts.count.n + (opts.count.dir === 'up' ? ' ▲' : ' ▼')));
      }
      return t;
    }

    var items = [];
    var sealedIdx = mode === 'sealed' && lastIdx;
    if (sealedIdx && lastIdx.overall) {
      items.push(item({ label: 'Quaza Index', value: fmtPts(groupLevel(lastIdx.overall)), chg: groupPct(lastIdx.overall), goidx: 'quaza' }));
    }
    items.push(item({ label: (mode === 'singles' ? 'Singles Market' : 'Pokémon Market') + ' · 7d', value: moved.length + ' products', chg: avg }));
    items.push(item({ label: 'Rising', count: { n: up, dir: 'up' } }));
    items.push(item({ label: 'Falling', count: { n: down, dir: 'down' } }));
    if (sealedIdx) {
      (lastIdx.types || []).forEach(function (t) {
        items.push(item({ label: (t.name || t.key) + ' Index', value: fmtPts(groupLevel(t)), chg: groupPct(t), goidx: t.key }));
      });
      var hot = (lastIdx.sets || []).slice()
        .sort(function (a, b) { return (groupPct(b) || -1e9) - (groupPct(a) || -1e9); })
        .slice(0, 3);
      hot.forEach(function (s) {
        items.push(item({ label: 'Hot Set', name: s.set_name, chg: groupPct(s), goidx: 'set:' + s.set_name }));
      });
    }
    // top movers, gainer/loser alternating for color rhythm
    var gains = sorted.slice(0, 3);
    var drops = sorted.slice(-3).reverse();
    for (var i = 0; i < 3; i++) {
      if (gains[i]) items.push(item({ name: gains[i].name, value: C.fmtCAD(gains[i].price_cents), chg: gains[i].change7d_pct, open: gains[i].id }));
      if (drops[i]) items.push(item({ name: drops[i].name, value: C.fmtCAD(drops[i].price_cents), chg: drops[i].change7d_pct, open: drops[i].id }));
    }

    statsEl.textContent = '';
    statsEl.classList.remove('qpt-tape--live');
    var track = el('div', 'qpt-tape-track');
    var half = el('div', 'qpt-tape-half');
    items.forEach(function (n) { half.appendChild(n); });
    track.appendChild(half);
    statsEl.appendChild(track);
    // the loop needs each half to be at least as wide as the viewport strip;
    // duplicate the chip run inside the half until it is. If the tape mounts
    // while the market view is hidden (deep-linked product/index page) the
    // width measures 0 — retry a few frames, and showMarket() re-arms later.
    var armTries = 0;
    function armTape() {
      if (!statsEl.contains(half)) return; // superseded by a newer render
      var baseW = half.scrollWidth;
      if (!baseW) {
        if (armTries++ < 40) requestAnimationFrame(armTape);
        return;
      }
      var copies = Math.max(1, Math.ceil(statsEl.clientWidth / baseW));
      for (var c = 1; c < copies; c++) {
        var again = half.cloneNode(true);
        again.setAttribute('aria-hidden', 'true');
        while (again.firstChild) half.appendChild(again.firstChild);
      }
      var mirror = half.cloneNode(true);
      mirror.setAttribute('aria-hidden', 'true');
      mirror.setAttribute('inert', '');
      var mirrorFocusables = mirror.querySelectorAll('button, a');
      for (var mf = 0; mf < mirrorFocusables.length; mf++) mirrorFocusables[mf].tabIndex = -1;
      track.appendChild(mirror);
      statsEl.style.setProperty('--qpt-tick-dur', Math.max(24, Math.round(half.scrollWidth / 42)) + 's');
      statsEl.classList.add('qpt-tape--live');
    }
    requestAnimationFrame(armTape);
  }

  /* ---------- market: index hero + set indexes (TradingView-style) ---------- */

  function fmtPts(v) { return v == null ? '—' : v.toFixed(2); }
  /* TradingView-style colored-text change (cards/hero); pills stay in tables */
  function chgText(pct) {
    var t = el('span', 'qpt-chgtext');
    if (pct == null) t.textContent = '—';
    else if (pct >= 0) { t.className += ' up'; t.textContent = '+' + pct + '%'; }
    else { t.className += ' down'; t.textContent = '−' + Math.abs(pct) + '%'; }
    return t;
  }

  /* TradingView-style carousel arrows on horizontal card rows */
  function enhanceCarousel(row) {
    if (!row) return;
    if (row.__qptCarUpdate) { row.__qptCarUpdate(); return; }
    var wrap = el('div', 'qpt-carousel');
    row.parentNode.insertBefore(wrap, row);
    wrap.appendChild(row);
    function mkBtn(dir, label) {
      var b = el('button', 'qpt-car-btn qpt-car-' + dir, dir === 'prev' ? '‹' : '›');
      b.type = 'button';
      b.setAttribute('aria-label', label);
      b.addEventListener('click', function () {
        row.scrollBy({ left: (dir === 'prev' ? -1 : 1) * row.clientWidth * 0.9, behavior: 'smooth' });
      });
      wrap.appendChild(b);
      return b;
    }
    var prev = mkBtn('prev', 'Scroll back');
    var next = mkBtn('next', 'Scroll forward');
    function update() {
      var max = row.scrollWidth - row.clientWidth - 2;
      prev.hidden = row.scrollLeft <= 2;
      next.hidden = row.scrollLeft >= max;
    }
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    row.__qptCarUpdate = update;
    requestAnimationFrame(update);
  }

  function idxChip(pct) {
    var c = el('span', 'qpt-chgpill');
    if (pct == null) { c.textContent = '—'; }
    else if (pct >= 0) { c.className += ' up'; c.textContent = '+' + pct + '%'; }
    else { c.className += ' down'; c.textContent = '−' + Math.abs(pct) + '%'; }
    return c;
  }

  function renderIndexHero(idx) {
    if (!idxMainEl) return;
    var o = idx.overall || {};
    idxMainEl.textContent = '';
    var head = el('div', 'qpt-idx-head');
    var left = el('div');
    left.appendChild(el('p', 'qpt-idx-label', (o.name || 'Quaza Pokémon Index') + ' · past 3 months'));
    var valRow = el('div', 'qpt-idx-valrow');
    valRow.appendChild(el('span', 'qpt-idx-value', fmtPts(o.level)));
    valRow.appendChild(el('span', 'qpt-idx-unit', 'PTS'));
    valRow.appendChild(chgText(o.change_pct));
    left.appendChild(valRow);
    head.appendChild(left);
    idxMainEl.appendChild(head);
    var chart = el('div', 'qpt-idx-chart');
    idxMainEl.appendChild(chart);
    var noteRow = el('p', 'qpt-idx-note');
    noteRow.appendChild(document.createTextNode(
      (o.curated ? 'Curated index of ' : 'Equal-weight index of ') +
      (o.count || '') + ' sealed products · base 100 · '));
    var view = el('button', 'qpt-idx-viewlink', 'View index page →');
    view.type = 'button';
    view.addEventListener('click', function () { navigateToIndex('quaza'); });
    noteRow.appendChild(view);
    idxMainEl.appendChild(noteRow);
    var valEl = valRow.querySelector('.qpt-idx-value');
    var chipEl = valRow.querySelector('.qpt-chgtext');
    var labelEl = left.querySelector('.qpt-idx-label');
    var restingLabel = labelEl.textContent;
    requestAnimationFrame(function () {
      // guides:false — dashed H/L labels crowded the index line; scrubbing
      // drives the big readout instead (mobile + desktop)
      C.drawChart(chart, o.series || [], (o.change_pct || 0) >= 0, {
        fmt: fmtPts,
        guides: false,
        onScrub: function (pt) {
          if (!pt) {
            valEl.textContent = fmtPts(o.level);
            labelEl.textContent = restingLabel;
            chipEl.replaceWith(chipEl = chgText(o.change_pct));
            return;
          }
          valEl.textContent = fmtPts(pt.value);
          labelEl.textContent = (o.name || 'Quaza Index') + ' · ' + C.fmtDate(pt.date);
          var d = pt.first > 0 ? Math.round(((pt.value - pt.first) / pt.first) * 1000) / 10 : null;
          chipEl.replaceWith(chipEl = chgText(d));
        }
      });
    });
  }

  /* ---------- index pages (quaza | type:<key> | set:<name>) ---------- */

  function resolveIndex(key, src) {
    var data = src || lastIdx;
    if (!data) return null;
    if (key === 'quaza') {
      var o = data.overall || {};
      var members;
      if (o.curated && o.ids) {
        var m = {};
        (o.ids || []).forEach(function (id) { m[id] = 1; });
        members = products.filter(function (p) { return m[p.id]; });
      } else {
        members = products.filter(function (p) { return p.type !== 'single'; });
      }
      return { key: key, name: o.name || 'Quaza Index', sub: o.curated ? 'Curated flagship index' : 'The whole sealed market, equal-weight',
        count: o.count, level: o.level, change_pct: o.change_pct, series: o.series, members: members };
    }
    var t = (data.types || []).filter(function (x) { return x.key === key; })[0];
    if (t) {
      var tm = {};
      (t.ids || []).forEach(function (id) { tm[id] = 1; });
      return { key: key, name: t.name + ' Index', sub: 'Every tracked ' + t.name.toLowerCase() + ', across all sets',
        count: t.count, level: t.level, change_pct: t.change_pct, series: t.series,
        members: products.filter(function (p) { return tm[p.id]; }) };
    }
    if (key.indexOf('set:') === 0) {
      var name = key.slice(4);
      var s = (data.sets || []).filter(function (x) { return x.set_name === name; })[0];
      if (s) {
        return { key: key, name: s.set_name, sub: 'Set index · sealed products only', setName: s.set_name,
          count: s.count, level: s.level, change_pct: s.change_pct, series: s.series,
          members: products.filter(function (p) { return p.set_name === s.set_name && p.type !== 'single'; }) };
      }
    }
    return null;
  }

  /* eye-catching route to the tracker's main market page — the owner wants
     product/index pages to pull people INTO the home view, not just step back */
  function trackerHomeBtn() {
    var b = el('button', 'qpt-home');
    b.type = 'button';
    b.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>' +
      '<span>Explore the Price Tracker</span><span class="qpt-home-arrow" aria-hidden="true">→</span>';
    b.addEventListener('click', function () {
      var u = new URL(location.href);
      u.searchParams.delete('product');
      u.searchParams.delete('index');
      history.pushState({}, '', u);
      showMarket();
      window.scrollTo({ top: 0 });
    });
    return b;
  }

  function renderIndexPage(ix) {
    productEl.textContent = '';
    productEl.appendChild(trackerHomeBtn());

    var head = el('div', 'qpt-pv-head');
    if (ix.setName) {
      var lg = setLogo(ix.setName, 44);
      if (lg) {
        var lgWrap = el('div', 'qpt-ix-headlogo');
        lgWrap.appendChild(lg);
        head.appendChild(lgWrap);
      }
    }
    var headTxt = el('div');
    var h1 = el('h1', 'qpt-pv-name', ix.name);
    h1.tabIndex = -1;
    headTxt.appendChild(h1);
    headTxt.appendChild(el('p', 'qpt-pv-meta', ix.sub + ' · ' + ix.count + ' products · base 100'));
    head.appendChild(headTxt);
    productEl.appendChild(head);

    C.injectCSS(); // the range pills reuse the chart widget's qpc-pill styles

    var IDXR = [['1w', '1W'], ['1m', '1M'], ['3m', '3M'], ['6m', '6M'], ['max', 'MAX']];
    var IDXR_TEXT = { '1w': 'past week', '1m': 'past month', '3m': 'past 3 months', '6m': 'past 6 months', 'max': 'all time' };
    var IDXR_LABEL = { '1w': '1-week change', '1m': '1-month change', '3m': '3-month change', '6m': '6-month change', 'max': 'All-time change' };
    var curRange = '3m';
    var cur = ix; // the resolved index for the active range

    var chartCard = el('div', 'qpt-pv-chartcard');
    var valRow = el('div', 'qpt-idx-valrow');
    var valEl = el('span', 'qpt-idx-value', fmtPts(ix.level));
    valRow.appendChild(valEl);
    valRow.appendChild(el('span', 'qpt-idx-unit', 'PTS'));
    var chipEl = chgText(ix.change_pct);
    valRow.appendChild(chipEl);
    var scrubDate = el('span', 'qpt-ix-scrubdate', IDXR_TEXT[curRange]);
    valRow.appendChild(scrubDate);
    chartCard.appendChild(valRow);
    var chart = el('div', 'qpt-idx-chart');
    chartCard.appendChild(chart);
    var ranges = el('div', 'qpc-ranges');
    ranges.setAttribute('role', 'radiogroup');
    ranges.setAttribute('aria-label', 'Index history range');
    IDXR.forEach(function (r) {
      var b = el('button', 'qpc-pill', r[1]);
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.setAttribute('data-range', r[0]);
      b.setAttribute('aria-checked', r[0] === curRange ? 'true' : 'false');
      b.addEventListener('click', function () { setRange(r[0]); });
      ranges.appendChild(b);
    });
    chartCard.appendChild(ranges);
    productEl.appendChild(chartCard);

    // six tiles, always, matching the product view's uniform grid; the first
    // two follow the selected range, the rest are range-independent
    var statRow = el('div', 'qpt-pv-statrow');
    productEl.appendChild(statRow);
    function paintIdxStats() {
      statRow.textContent = '';
      statRow.appendChild(statTile('Index level', fmtPts(cur.level)));
      statRow.appendChild(statTile(IDXR_LABEL[curRange],
        cur.change_pct == null ? '—' : (cur.change_pct >= 0 ? '+' : '−') + Math.abs(cur.change_pct) + '%',
        cur.change_pct == null ? null : cur.change_pct >= 0 ? 'qpt-up' : 'qpt-down'));
      statRow.appendChild(statTile('Products', String(ix.count)));
      // basket value: what one of everything in this index costs today
      var basket = 0;
      ix.members.forEach(function (p) { if (p.price_cents != null) basket += p.price_cents; });
      statRow.appendChild(statTile('Basket value', basket ? C.fmtCAD(basket) : '—'));
      var movers7 = ix.members.filter(function (p) { return p.change7d_pct != null; })
        .sort(function (a, b) { return b.change7d_pct - a.change7d_pct; });
      var best = movers7[0];
      statRow.appendChild(statTile('Top gainer · 7d',
        best ? (best.change7d_pct >= 0 ? '+' : '−') + Math.abs(best.change7d_pct) + '%' : '—',
        best && best.change7d_pct >= 0 ? 'qpt-up' : best ? 'qpt-down' : null));
      var up7 = 0, down7 = 0;
      movers7.forEach(function (p) {
        if (p.change7d_pct > 0) up7++; else if (p.change7d_pct < 0) down7++;
      });
      statRow.appendChild(statTile('Rising / falling · 7d', movers7.length ? up7 + ' ▲ · ' + down7 + ' ▼' : '—'));
    }
    paintIdxStats();

    function drawIdx() {
      C.drawChart(chart, cur.series || [], (cur.change_pct || 0) >= 0, {
        fmt: fmtPts,
        guides: false,
        onScrub: function (pt) {
          if (!pt) {
            valEl.textContent = fmtPts(cur.level);
            scrubDate.textContent = IDXR_TEXT[curRange];
            chipEl.replaceWith(chipEl = chgText(cur.change_pct));
            return;
          }
          valEl.textContent = fmtPts(pt.value);
          scrubDate.textContent = C.fmtDate(pt.date);
          var d = pt.first > 0 ? Math.round(((pt.value - pt.first) / pt.first) * 1000) / 10 : null;
          chipEl.replaceWith(chipEl = chgText(d));
        }
      });
    }

    function setRange(r) {
      if (r === curRange) return;
      curRange = r;
      ranges.querySelectorAll('.qpc-pill').forEach(function (b) {
        b.setAttribute('aria-checked', b.getAttribute('data-range') === r ? 'true' : 'false');
      });
      function apply(payload) {
        if (curRange !== r) return; // a faster later click won
        var next = resolveIndex(ix.key, payload);
        if (!next) return;
        cur = next;
        valEl.textContent = fmtPts(cur.level);
        scrubDate.textContent = IDXR_TEXT[r];
        chipEl.replaceWith(chipEl = chgText(cur.change_pct));
        paintIdxStats();
        drawIdx();
      }
      if (idxPayloadCache[r]) { apply(idxPayloadCache[r]); return; }
      fetch(API + '/indexes?range=' + r, { headers: { Accept: 'application/json' } })
        .then(function (res) { if (!res.ok) throw new Error(String(res.status)); return res.json(); })
        .then(function (payload) { idxPayloadCache[r] = payload; apply(payload); })
        .catch(function () { /* keep the current range on failure */ });
    }

    productEl.appendChild(el('h2', 'qpt-pv-relhead', 'Products in this index (' + ix.members.length + ')'));
    var grid = el('div', 'qpt-grid');
    ix.members.slice().sort(function (a, b) { return (b.price_cents || 0) - (a.price_cents || 0); })
      .forEach(function (p) { grid.appendChild(productCard(p)); });
    productEl.appendChild(grid);
    productEl.appendChild(el('p', 'qpt-d-note qpt-ix-foot',
      'Equal-weight index, base 100 at the start of the range. Market data via TCGplayer, updated daily.'));

    requestAnimationFrame(drawIdx);

    document.title = ix.name + ' — ' + baseTitle;
    window.scrollTo(0, 0);
    h1.focus({ preventScroll: true });
  }

  /* ---------- market: product-type index cards ---------- */

  function renderTypeCards(idx) {
    if (!typeCardsEl) return;
    typeCardsEl.textContent = '';
    (idx.types || []).forEach(function (t) {
      var card = el('div', 'qpt-idxcard');
      var head = el('div', 'qpt-idxcard-head');
      var nameWrap = el('div', 'qpt-idxcard-namewrap');
      var monoText = { etb: 'ETB', 'booster-bundle': 'BB', 'booster-box': 'BX', collection: 'CB', upc: 'UPC' }[t.key] || 'IX';
      nameWrap.appendChild(el('span', 'qpt-monogram', monoText));
      nameWrap.appendChild(el('p', 'qpt-idxcard-name', t.name + ' Index'));
      head.appendChild(nameWrap);
      head.appendChild(el('span', 'qpt-idxcard-badge', 'INDEX'));
      card.appendChild(head);
      var valRow = el('div', 'qpt-idx-valrow qpt-idxcard-valrow');
      valRow.appendChild(el('span', 'qpt-idxcard-level', fmtPts(t.level)));
      valRow.appendChild(el('span', 'qpt-idx-unit', 'PTS'));
      valRow.appendChild(chgText(t.change_pct));
      card.appendChild(valRow);
      var mini = el('div', 'qpt-idxcard-chart');
      card.appendChild(mini);
      card.appendChild(el('p', 'qpt-chartcaption', 'Past 3 months'));
      var memberMap = {};
      (t.ids || []).forEach(function (id) { memberMap[id] = 1; });
      var top = products.filter(function (p) { return memberMap[p.id] && p.price_cents != null; })
        .sort(function (a, b) { return b.price_cents - a.price_cents; }).slice(0, 3);
      top.forEach(function (p) {
        var row = el('button', 'qpt-idxcard-prod');
        row.type = 'button';
        row.setAttribute('data-open', p.id);
        var im = el('span', 'qpt-idxcard-prodimg');
        im.appendChild(img(p.image_url, '', 28));
        row.appendChild(im);
        row.appendChild(el('span', 'qpt-idxcard-prodname', p.name));
        var right = el('span', 'qpt-idxcard-prodright');
        right.appendChild(el('span', 'qpt-idxcard-prodprice', C.fmtCAD(p.price_cents)));
        right.appendChild(chip(p.change7d_pct));
        row.appendChild(right);
        card.appendChild(row);
      });
      var see = el('button', 'qpt-idxcard-see', 'View index page →');
      see.type = 'button';
      see.addEventListener('click', function () { navigateToIndex(t.key); });
      card.appendChild(see);
      typeCardsEl.appendChild(card);
      lazyChart(mini, function () {
        C.drawChart(mini, t.series || [], (t.change_pct || 0) >= 0, { fmt: fmtPts, minimal: true });
      });
    });
    enhanceCarousel(typeCardsEl);
  }

  var MISC_SET = 'Miscellaneous Cards & Products';
  /* set indexes ranked for display: best performers first; the misc
     catch-all always sinks to the bottom (it's a bucket, not a set) */
  function rankedSets(sets) {
    return (sets || []).slice().sort(function (a, b) {
      var ma = a.set_name === MISC_SET ? 1 : 0, mb = b.set_name === MISC_SET ? 1 : 0;
      if (ma !== mb) return ma - mb;
      var ca = a.change_pct != null ? a.change_pct : -1e9;
      var cb = b.change_pct != null ? b.change_pct : -1e9;
      return cb - ca;
    });
  }

  /* sidebar list order: newest set first (release recency), misc last */
  function newestSets(sets) {
    return (sets || []).slice().sort(function (a, b) {
      var ma = a.set_name === MISC_SET ? 1 : 0, mb = b.set_name === MISC_SET ? 1 : 0;
      if (ma !== mb) return ma - mb;
      return setRel(a.set_name) < setRel(b.set_name) ? 1 : -1;
    });
  }

  function setIdxRow(s) {
    var b = el('button', 'qpt-idxrow-item');
    b.type = 'button';
    b.addEventListener('click', function () { navigateToIndex('set:' + s.set_name); });
    var lg = setLogo(s.set_name, 20);
    if (lg) {
      var lgWrap = el('span', 'qpt-idxrow-logo');
      lgWrap.appendChild(lg);
      b.appendChild(lgWrap);
    }
    var left = el('div');
    left.appendChild(el('p', 'qpt-idxrow-name', s.set_name));
    left.appendChild(el('p', 'qpt-idxrow-sub', s.count + ' products'));
    var right = el('div', 'qpt-idxrow-right');
    right.appendChild(el('p', 'qpt-idxrow-level', fmtPts(s.level)));
    right.appendChild(chgText(s.change_pct));
    b.appendChild(left);
    b.appendChild(right);
    return b;
  }

  function renderIndexList(idx) {
    if (!idxListEl) return;
    idxListEl.textContent = '';
    idxListEl.appendChild(el('h2', 'qpt-idxlist-h', 'Set indexes'));
    // the visible shortlist is a showcase: newest logo-bearing sets only.
    // The complete directory (incl. logo-less sets) is its own page.
    newestSets(idx.sets)
      .filter(function (s) { return hasSetLogo(s.set_name); })
      .slice(0, 7)
      .forEach(function (s) { idxListEl.appendChild(setIdxRow(s)); });
    var more = el('button', 'qpt-seeall', 'See all ' + idx.sets.length + ' set indexes →');
    more.type = 'button';
    more.addEventListener('click', function () { navigateToIndex('sets'); });
    idxListEl.appendChild(more);
  }

  /* full set-index directory (?index=sets) — every set, newest first, misc last */
  function renderAllSetsPage(idx) {
    productEl.textContent = '';
    productEl.appendChild(trackerHomeBtn());
    // the directory is a narrow column — centered, like the rest of the site
    var wrap = el('div', 'qpt-allsets-wrap');
    var head = el('div', 'qpt-pv-head');
    var headTxt = el('div');
    var h1 = el('h1', 'qpt-pv-name', 'Set Indexes');
    h1.tabIndex = -1;
    headTxt.appendChild(h1);
    headTxt.appendChild(el('p', 'qpt-pv-meta', idx.sets.length + ' set indexes · newest sets first · base 100'));
    head.appendChild(headTxt);
    wrap.appendChild(head);
    var list = el('div', 'qpt-idxlist qpt-allsets');
    newestSets(idx.sets).forEach(function (s) { list.appendChild(setIdxRow(s)); });
    wrap.appendChild(list);
    productEl.appendChild(wrap);
    document.title = 'Set Indexes — ' + baseTitle;
  }

  function renderIndexCards(idx, showAll) {
    if (!idxCardsEl) return;
    idxCardsEl.textContent = '';
    // the cards row is the visual showcase: only sets with official logos get a
    // card. Logo-less sets stay reachable via the sidebar list and Indices nav.
    var rankedCards = rankedSets(idx.sets).filter(function (s) { return hasSetLogo(s.set_name); });
    var shownCards = showAll
      ? rankedCards
      : rankedCards.filter(function (s) { return s.count >= 4; }).slice(0, 8);
    shownCards.forEach(function (s) {
      var card = el('div', 'qpt-idxcard');
      var head = el('div', 'qpt-idxcard-head');
      var nameWrap = el('div', 'qpt-idxcard-namewrap');
      var lg = setLogo(s.set_name, 26);
      if (lg) nameWrap.appendChild(lg);
      nameWrap.appendChild(el('p', 'qpt-idxcard-name', s.set_name));
      var badge = el('span', 'qpt-idxcard-badge', 'INDEX');
      head.appendChild(nameWrap);
      head.appendChild(badge);
      card.appendChild(head);
      var valRow = el('div', 'qpt-idx-valrow qpt-idxcard-valrow');
      valRow.appendChild(el('span', 'qpt-idxcard-level', fmtPts(s.level)));
      valRow.appendChild(el('span', 'qpt-idx-unit', 'PTS'));
      valRow.appendChild(chgText(s.change_pct));
      card.appendChild(valRow);
      var mini = el('div', 'qpt-idxcard-chart');
      card.appendChild(mini);
      card.appendChild(el('p', 'qpt-chartcaption', 'Past 3 months'));
      // top products of the set by latest price
      var top = products.filter(function (p) { return p.set_name === s.set_name && p.price_cents != null; })
        .sort(function (a, b) { return b.price_cents - a.price_cents; }).slice(0, 3);
      top.forEach(function (p) {
        var row = el('button', 'qpt-idxcard-prod');
        row.type = 'button';
        row.setAttribute('data-open', p.id);
        var im = el('span', 'qpt-idxcard-prodimg');
        im.appendChild(img(p.image_url, '', 28));
        row.appendChild(im);
        row.appendChild(el('span', 'qpt-idxcard-prodname', p.name));
        var right = el('span', 'qpt-idxcard-prodright');
        right.appendChild(el('span', 'qpt-idxcard-prodprice', C.fmtCAD(p.price_cents)));
        right.appendChild(chip(p.change7d_pct));
        row.appendChild(right);
        card.appendChild(row);
      });
      var see = el('button', 'qpt-idxcard-see', 'View index page →');
      see.type = 'button';
      see.addEventListener('click', function () { navigateToIndex('set:' + s.set_name); });
      card.appendChild(see);
      idxCardsEl.appendChild(card);
      lazyChart(mini, function () {
        C.drawChart(mini, s.series || [], (s.change_pct || 0) >= 0, { fmt: fmtPts, minimal: true });
      });
    });
    enhanceCarousel(idxCardsEl);
    var cardsSec = idxCardsEl.closest('section') || idxCardsEl.parentNode;
    var oldMore = cardsSec.querySelector('.qpt-idxcards-more');
    if (oldMore) oldMore.remove();
    if (rankedCards.length > shownCards.length || showAll) {
      var moreBtn = el('button', 'qpt-seeall qpt-idxcards-more',
        showAll ? 'Show fewer' : 'See all ' + rankedCards.length + ' set indexes →');
      moreBtn.type = 'button';
      moreBtn.addEventListener('click', function () { renderIndexCards(idx, !showAll); });
      cardsSec.appendChild(moreBtn);
    }
  }

  /* ---------- market: gainers / losers lists ---------- */

  function renderLists() {
    if (!gainersEl || !losersEl) return;
    var ranked = products
      .filter(function (p) { return inMode(p) && p.change7d_pct != null && p.price_cents != null; })
      .sort(function (a, b) { return b.change7d_pct - a.change7d_pct; });
    function fill(mount, list) {
      mount.textContent = '';
      list.forEach(function (p) {
        var row = el('button', 'qpt-listrow');
        row.type = 'button';
        row.setAttribute('data-open', p.id);
        var im = el('span', 'qpt-listrow-img');
        im.appendChild(img(p.image_url, '', 36));
        row.appendChild(im);
        var mid = el('span', 'qpt-listrow-mid');
        mid.appendChild(el('span', 'qpt-listrow-name', p.name));
        if (p.set_name) mid.appendChild(el('span', 'qpt-ticker', p.set_name));
        row.appendChild(mid);
        var right = el('span', 'qpt-listrow-right');
        var priceWrap = el('span', 'qpt-listrow-price');
        priceWrap.appendChild(document.createTextNode(C.fmtCAD(p.price_cents)));
        priceWrap.appendChild(el('span', 'qpt-unit', C.moneyCode()));
        right.appendChild(priceWrap);
        right.appendChild(idxChip(p.change7d_pct));
        row.appendChild(right);
        mount.appendChild(row);
      });
    }
    fill(gainersEl, ranked.slice(0, 5));
    fill(losersEl, ranked.slice(-5).reverse());
    [gainersEl, losersEl].forEach(function (mount, i) {
      /* only the list mount is cleared above — drop the old closer or re-renders stack them */
      var stale = mount.parentElement.querySelector('.qpt-seeall');
      if (stale) stale.remove();
      var see = el('button', 'qpt-seeall', i === 0 ? 'Sort all by 7d gainers →' : 'Sort all by 7d losers →');
      see.type = 'button';
      see.addEventListener('click', function () {
        if (sortEl) { sortEl.value = i === 0 ? 'change-desc' : 'change-asc'; sortEl.dispatchEvent(new Event('change')); }
        var browse = document.getElementById('qpt-browse');
        if (browse) browse.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      mount.parentElement.appendChild(see);
    });
  }

  /* ---------- market: era tabs + browse-by-set strip ---------- */

  function renderEraTabs() {
    if (!erasEl) return;
    erasEl.textContent = '';
    var seen = {}, eras = [];
    products.forEach(function (p) {
      if (!inMode(p)) return;
      var e = eraOf(p);
      if (!seen[e]) { seen[e] = 1; eras.push(e); }
    });
    eras.sort(function (a, b) { return (ERA_RANK[a] || 0) - (ERA_RANK[b] || 0); });
    function tab(label, value) {
      var b = el('button', 'qpt-eratab');
      b.type = 'button';
      b.textContent = label;
      b.setAttribute('aria-pressed', (value === activeEra || (!value && !activeEra)) ? 'true' : 'false');
      b.addEventListener('click', function () { applyEraSet(value, null, false); });
      erasEl.appendChild(b);
    }
    tab('All', null);
    eras.forEach(function (e) { tab(eraLabel(e), e); });
  }

  function renderSetStrip() {
    if (!setsEl) return;
    setsEl.textContent = '';
    // unique sets, newest release first, narrowed to the active era filter
    var seen = {}, sets = [];
    products.forEach(function (p) {
      if (!inMode(p)) return;
      if (activeEra && eraOf(p) !== activeEra) return;
      var s = p.set_name || 'Other';
      if (!seen[s]) { seen[s] = { name: s, count: 0 }; sets.push(seen[s]); }
      seen[s].count++;
    });
    sets.sort(function (a, b) { return setRel(a.name) < setRel(b.name) ? 1 : -1; });

    function setChip(label, value, count) {
      var b = el('button', 'qpt-setchip');
      b.type = 'button';
      if (value) {
        var lg = setLogo(value, 20);
        if (lg) b.appendChild(lg);
      }
      b.appendChild(document.createTextNode(label));
      if (count != null) {
        var small = document.createElement('small');
        small.textContent = String(count);
        b.appendChild(small);
      }
      b.setAttribute('aria-pressed', (value === activeSet || (!value && !activeSet)) ? 'true' : 'false');
      b.addEventListener('click', function () { applyEraSet(activeEra, value, false); });
      return b;
    }

    var allCount = products.filter(function (p) {
      return inMode(p) && (!activeEra || eraOf(p) === activeEra);
    }).length;
    setsEl.appendChild(setChip(activeEra ? 'All ' + eraLabel(activeEra) : 'All sets', null, allCount));
    sets.forEach(function (s) { setsEl.appendChild(setChip(s.name, s.name, s.count)); });
  }

  /* ---------- market: catalog grid ---------- */

  var SORTS = {
    'newest': function (a, b) {
      var ra = setRel(a.set_name), rb = setRel(b.set_name);
      if (ra !== rb) return ra < rb ? 1 : -1;
      return (b.price_cents || 0) - (a.price_cents || 0);
    },
    'price-desc': function (a, b) { return (b.price_cents || 0) - (a.price_cents || 0); },
    'price-asc': function (a, b) { return (a.price_cents || 1e15) - (b.price_cents || 1e15); },
    'change-desc': function (a, b) { return (b.change7d_pct != null ? b.change7d_pct : -1e9) - (a.change7d_pct != null ? a.change7d_pct : -1e9); },
    'change-asc': function (a, b) { return (a.change7d_pct != null ? a.change7d_pct : 1e9) - (b.change7d_pct != null ? b.change7d_pct : 1e9); },
    'name': function (a, b) { return a.name.localeCompare(b.name); }
  };

  function productCard(p) {
    var card = el('button', 'qpt-card');
    card.type = 'button';
    card.setAttribute('data-open', p.id);
    var im = el('div', 'qpt-card-img');
    im.appendChild(img(p.image_url, p.name, 160));
    card.appendChild(im);
    card.appendChild(el('p', 'qpt-card-name', p.name));
    var foot = el('div', 'qpt-card-foot');
    foot.appendChild(el('span', 'qpt-card-price', C.fmtCAD(p.price_cents)));
    foot.appendChild(chip(p.change7d_pct));
    card.appendChild(foot);
    return card;
  }

  function filtersActive() {
    return !!(query || lang !== 'all' || activeEra || activeSet || activeType || sort !== 'featured');
  }

  /* one reset shared by the toolbar chip and the empty-state button */
  function resetFilters() {
    query = '';
    if (searchEl) searchEl.value = '';
    lang = 'all';
    if (filtersEl) {
      filtersEl.querySelectorAll('[data-lang]').forEach(function (pill) {
        pill.setAttribute('aria-pressed', pill.getAttribute('data-lang') === 'all' ? 'true' : 'false');
      });
    }
    sort = 'featured';
    if (sortEl) sortEl.value = 'featured';
    applyType(null, false); // also clears era/set and re-renders everything
  }

  function renderCatalog() {
    catalogEl.textContent = '';
    // the language toggle only earns its place when the current market actually
    // has more than one language (singles are EN-only today; data-driven so JP
    // singles would bring it back automatically)
    if (filtersEl) {
      var hasJP = products.some(function (p) { return inMode(p) && p.language === 'JP'; });
      if (!hasJP && lang !== 'all') {
        lang = 'all';
        filtersEl.querySelectorAll('[data-lang]').forEach(function (pill) {
          pill.setAttribute('aria-pressed', pill.getAttribute('data-lang') === 'all' ? 'true' : 'false');
        });
      }
      filtersEl.hidden = !hasJP;
    }
    var shown = products.filter(matches);

    if (sort !== 'featured') {
      // flat sorted grid, TCGplayer-style
      var flat = shown.slice().sort(SORTS[sort] || SORTS.name);
      var grid = el('div', 'qpt-grid');
      flat.forEach(function (p) { grid.appendChild(productCard(p)); });
      catalogEl.appendChild(grid);
    } else {
      // grouped era → set, newest release first ("proper" catalog order)
      var ordered = shown.slice().sort(function (a, b) {
        var ea = eraOf(a), eb = eraOf(b);
        if (ea !== eb) return (ERA_RANK[ea] || 0) - (ERA_RANK[eb] || 0);
        var ra = setRel(a.set_name), rb = setRel(b.set_name);
        if (ra !== rb) return ra < rb ? 1 : -1;
        if (a.set_name !== b.set_name) return (a.set_name || '').localeCompare(b.set_name || '');
        return (b.price_cents || 0) - (a.price_cents || 0);
      });
      var currentEra = null, currentSet = null, grid2 = null;
      var setCounts = {};
      ordered.forEach(function (p) {
        var k = (p.set_name || 'Other') + (p.language === 'JP' ? ' · Japanese' : '');
        setCounts[k] = (setCounts[k] || 0) + 1;
      });
      ordered.forEach(function (p) {
        var eraL = eraLabel(eraOf(p));
        if (eraL !== currentEra) {
          currentEra = eraL;
          currentSet = null;
          catalogEl.appendChild(el('h3', 'qpt-era', eraL));
        }
        var setLabel = (p.set_name || 'Other') + (p.language === 'JP' ? ' · Japanese' : '');
        if (setLabel !== currentSet) {
          currentSet = setLabel;
          var head = el('h4', 'qpt-set');
          head.appendChild(el('span', 'qpt-set-name', setLabel));
          head.appendChild(el('span', 'qpt-set-n', String(setCounts[setLabel])));
          // jump straight to this set's index page (indexes track sealed sets only,
          // so the link renders only when the index actually exists in lastIdx)
          if (mode === 'sealed' && p.set_name && lastIdx && lastIdx.sets &&
              lastIdx.sets.some(function (s) { return s.set_name === p.set_name; })) {
            var ix = el('button', 'qpt-set-idx', 'Set index →');
            ix.type = 'button';
            ix.setAttribute('data-goidx', 'set:' + p.set_name);
            head.appendChild(ix);
          }
          catalogEl.appendChild(head);
          grid2 = el('div', 'qpt-grid');
          catalogEl.appendChild(grid2);
        }
        grid2.appendChild(productCard(p));
      });
    }

    if (typechipEl) {
      var def = activeType ? ptypeDef(activeType) : null;
      typechipEl.hidden = !def;
      if (def) typechipEl.textContent = 'Viewing: ' + def.label + '  ✕';
    }
    var denom = products.filter(inMode).length;
    countEl.textContent = shown.length === denom
      ? denom + ' products'
      : shown.length + ' of ' + denom + ' products';
    if (clearEl) clearEl.hidden = !filtersActive();
    if (!shown.length) {
      var status = el('div', 'qpt-status');
      status.appendChild(el('p', null, 'No products match your filters.'));
      var clear = el('button', 'qpt-clearbtn', 'Clear all filters');
      clear.type = 'button';
      clear.addEventListener('click', resetFilters);
      status.appendChild(clear);
      catalogEl.appendChild(status);
    }
  }

  /* ---------- product view ---------- */

  var widget = null;

  function sanitizeStoreHtml(html) {
    var doc = new DOMParser().parseFromString(html || '', 'text/html');
    var bad = doc.querySelectorAll('script, style, iframe, object, embed, link, form');
    for (var i = 0; i < bad.length; i++) bad[i].parentNode.removeChild(bad[i]);
    var all = doc.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      var attrs = all[j].attributes;
      for (var k = attrs.length - 1; k >= 0; k--) {
        var name = attrs[k].name.toLowerCase();
        var val = String(attrs[k].value || '');
        if (name.indexOf('on') === 0 || (name === 'href' || name === 'src') && val.trim().toLowerCase().indexOf('javascript:') === 0) {
          all[j].removeAttribute(attrs[k].name);
        }
      }
    }
    return doc.body.innerHTML;
  }

  function statTile(label, value, cls) {
    var t = el('div', 'qpt-pv-stat');
    t.appendChild(el('p', 'l', label));
    t.appendChild(el('p', 'v' + (cls ? ' ' + cls : ''), value));
    return t;
  }

  /* Store prices from Shopify's localized endpoints (/products/<h>.js,
     /recommendations/products.json) are ALREADY in the visitor's selected store
     currency — format them as-is with Shopify's active currency code. Never run
     them through fmtCAD: that applies the tracker's market-FX rate on top and
     double-converts (the Pitch Black PC ETB bug). Market prices keep fmtCAD. */
  function fmtStorePrice(cents) {
    if (cents == null) return '';
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* Native add-to-cart: same /cart/add.js + ajaxProduct:added flow as the theme's
     product forms, so the site's cart drawer opens and the header count updates. */
  function addToCart(variantId, btn) {
    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var original = btn.textContent;
    btn.disabled = true;
    btn.classList.add('btn--loading');
    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        btn.classList.remove('btn--loading');
        btn.disabled = false;
        if (!res.ok || res.d.status === 422) {
          btn.textContent = 'Sold Out';
          setTimeout(function () { btn.textContent = original; }, 2500);
          return;
        }
        if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page' &&
            window.theme.routes && window.theme.routes.cartPage) {
          window.location = window.theme.routes.cartPage;
          return;
        }
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        btn.textContent = 'Added ✓';
        setTimeout(function () { btn.textContent = original; }, 2000);
      })
      .catch(function () {
        btn.classList.remove('btn--loading');
        btn.disabled = false;
        btn.textContent = original;
      });
  }

  function renderProductView(p) {
    productEl.textContent = '';
    productEl.appendChild(trackerHomeBtn());

    var grid = el('div', 'qpt-pv');
    var main = el('div');
    var side = el('div', 'qpt-pv-side');
    grid.appendChild(main);
    grid.appendChild(side);
    productEl.appendChild(grid);

    // main column — product identity header (image first: this is what you're tracking)
    var head = el('div', 'qpt-pv-head');
    var headImg = el('div', 'qpt-pv-headimg');
    headImg.appendChild(img(p.image_url, p.name, 110));
    head.appendChild(headImg);
    var headTxt = el('div');
    var h1 = el('h1', 'qpt-pv-name', p.name);
    h1.tabIndex = -1;
    headTxt.appendChild(h1);
    headTxt.appendChild(el('p', 'qpt-pv-meta', metaLine(p)));
    head.appendChild(headTxt);
    main.appendChild(head);

    var chartCard = el('div', 'qpt-pv-chartcard');
    var chartMount = el('div');
    chartCard.appendChild(chartMount);
    main.appendChild(chartCard);

    var statRow = el('div', 'qpt-pv-statrow');
    main.appendChild(statRow);
    function paintStats(detail) {
      statRow.textContent = '';
      statRow.appendChild(statTile('Market price', C.fmtCAD(detail.current_cents)));
      if (p.change7d_pct != null) {
        statRow.appendChild(statTile('7-day change',
          (p.change7d_pct >= 0 ? '+' : '−') + Math.abs(p.change7d_pct) + '%',
          p.change7d_pct >= 0 ? 'qpt-up' : 'qpt-down'));
      } else {
        var rc = detail.change_pct;
        statRow.appendChild(statTile('Range change',
          rc == null ? '—' : (rc >= 0 ? '+' : '−') + Math.abs(rc) + '%',
          rc == null ? null : rc >= 0 ? 'qpt-up' : 'qpt-down'));
      }
      statRow.appendChild(statTile('Range high', C.fmtCAD(detail.high_cents)));
      statRow.appendChild(statTile('Range low', C.fmtCAD(detail.low_cents)));
      // always six tiles so the grid stays uniform (2×3 mobile, 3×2 up):
      // set rank = price position among same-type products of the set
      var peers = products.filter(function (o) {
        return o.set_name === p.set_name && (o.type === 'single') === (p.type === 'single') && o.price_cents != null;
      }).sort(function (a, b) { return b.price_cents - a.price_cents; });
      var rank = -1;
      peers.forEach(function (o, i) { if (o.id === p.id) rank = i; });
      statRow.appendChild(statTile('Set rank', rank >= 0 ? '#' + (rank + 1) + ' of ' + peers.length : '—'));
      var rel = setRel(p.set_name);
      statRow.appendChild(statTile('Released', rel
        ? new Date(rel + 'T12:00:00Z').toLocaleDateString('en-CA', { month: 'short', year: 'numeric', timeZone: 'UTC' })
        : '—'));
    }

    var descWrap = el('div', 'qpt-pv-desc');
    main.appendChild(descWrap);

    // bottom: related rows (same-set tracker products + store shop slider)
    var related = el('div', 'qpt-pv-related');
    productEl.appendChild(related);
    renderSameSetRow(p, related);
    var shopMount = el('div');
    related.appendChild(shopMount);

    // side column — buy/store card
    var card = el('div', 'qpt-pv-card');
    side.appendChild(card);

    if (p.shopify_handle) {
      card.appendChild(el('p', 'qpt-pv-cardlabel', 'At Quaza Collect'));
      var priceP = el('p', 'qpt-pv-storeprice', '…');
      var stockP = el('p', 'qpt-pv-stock', '');
      card.appendChild(priceP);
      card.appendChild(stockP);
      var ctaWrap = el('div');
      card.appendChild(ctaWrap);
      var cta = el('a', 'btn qpt-cta', 'Buy at Quaza Collect');
      var productUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') +
        'products/' + encodeURIComponent(p.shopify_handle);
      cta.href = productUrl;
      ctaWrap.appendChild(cta);

      // enrich from the store's public product JSON (same-origin, no tokens)
      var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootPath + 'products/' + encodeURIComponent(p.shopify_handle) + '.js')
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (sp) {
          priceP.textContent = fmtStorePrice(sp.price);
          stockP.textContent = sp.available ? 'In stock' : 'Sold out';
          stockP.className = 'qpt-pv-stock ' + (sp.available ? 'in' : 'out');
          if (sp.variants && sp.variants.length === 1 && sp.variants[0].available) {
            // single-variant in-stock: real add-to-cart, right here — the theme's
            // cart drawer takes over after the add, same as any PDP
            ctaWrap.textContent = '';
            var addBtn = el('button', 'btn qpt-cta', 'Add to Cart');
            addBtn.type = 'button';
            addBtn.addEventListener('click', function () { addToCart(sp.variants[0].id, addBtn); });
            ctaWrap.appendChild(addBtn);
            var view = el('a', 'qpt-cta-view', 'View full listing →');
            view.href = productUrl;
            ctaWrap.appendChild(view);
          } else if (!sp.available) {
            cta.textContent = 'View at Quaza Collect';
          }
          if (sp.description) {
            descWrap.appendChild(el('h3', null, 'About this product'));
            var body = el('div', 'qpt-pv-desc-body');
            body.innerHTML = sanitizeStoreHtml(sp.description);
            descWrap.appendChild(body);
          }
          renderShopSlider(shopMount, p, sp.id);
        })
        .catch(function () {
          // The tracker knows this handle but the storefront won't serve it:
          // the listing has been drafted, unpublished or removed. Track still
          // shows the market data, but the buy button has to go — otherwise it
          // sends people to a 404.
          var label = card.querySelector('.qpt-pv-cardlabel');
          if (label) label.textContent = 'Market data';
          priceP.textContent = C.fmtCAD(p.price_cents);
          stockP.textContent = 'Not currently sold at Quaza Collect';
          stockP.className = 'qpt-pv-stock out';
          ctaWrap.textContent = '';
          renderShopSlider(shopMount, p, null);
        });
    } else {
      card.appendChild(el('p', 'qpt-pv-cardlabel', 'Market data'));
      card.appendChild(el('p', 'qpt-pv-storeprice', C.fmtCAD(p.price_cents)));
      var note = el('p', 'qpt-pv-stock', 'Not currently sold at Quaza Collect');
      card.appendChild(note);
      renderShopSlider(shopMount, p, null);
    }
    card.appendChild(el('p', 'qpt-pv-note',
      'Market price via TCGplayer, quoted in CAD' +
      (C.moneyCode() !== 'CAD' ? ', shown in ' + C.moneyCode() : '') +
      ', updated daily. ' + updatedAtText +
      'Quaza Collect pricing and availability may differ.'));

    widget = C.renderWidget(chartMount, {
      endpoint: API + '/prices/' + encodeURIComponent(p.id),
      range: '3m',
      onDetail: paintStats
    });

    document.title = p.name + ' Price — ' + baseTitle;
    window.scrollTo(0, 0);
    h1.focus({ preventScroll: true });
  }

  /* ---------- product view: related rows ---------- */

  function relatedHeading(text) { return el('h3', 'qpt-pv-relhead', text); }

  function renderSameSetRow(p, mount) {
    var same = products.filter(function (o) {
      return o.set_name === p.set_name && o.id !== p.id;
    }).slice(0, 10);
    if (!same.length) return;
    mount.appendChild(relatedHeading('More in ' + (p.set_name || 'this set')));
    var row = el('div', 'qpt-shop-row');
    same.forEach(function (o) { row.appendChild(productCard(o)); });
    mount.appendChild(row);
  }

  /* Store slider: Shopify's related-products API when this product is in the store,
     otherwise the configured collection — both public same-origin JSON. */
  function renderShopSlider(mount, p, storeProductId) {
    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var collection = root.getAttribute('data-shop-collection') || 'sealed-pokemon';
    var heading = root.getAttribute('data-shop-heading') || 'Shop at Quaza Collect';

    // /products/<h>.js format: price in cents, featured_image
    function fromJsFormat(list) {
      return list.map(function (sp) {
        return { title: sp.title, handle: sp.handle, cents: sp.price, store: true, image: sp.featured_image, available: sp.available };
      });
    }
    // /collections/<h>/products.json format: price as "379.99" string, images array
    function fromRestFormat(list) {
      return list.map(function (sp) {
        var v = (sp.variants && sp.variants[0]) || {};
        return {
          title: sp.title, handle: sp.handle,
          cents: v.price != null ? Math.round(parseFloat(v.price) * 100) : null,
          image: (sp.images && sp.images[0] && sp.images[0].src) || null,
          available: v.available !== false
        };
      });
    }

    function paint(items) {
      items = items.filter(function (it) { return it.handle !== p.shopify_handle; }).slice(0, 8);
      if (!items.length) return;
      mount.appendChild(relatedHeading(heading));
      var row = el('div', 'qpt-shop-row');
      items.forEach(function (it) {
        var a = el('a', 'qpt-shopcard');
        a.href = rootPath + 'products/' + encodeURIComponent(it.handle);
        var im = el('div', 'qpt-shopcard-img');
        im.appendChild(img(it.image, it.title, 160));
        a.appendChild(im);
        a.appendChild(el('p', 'qpt-shopcard-name', it.title));
        var foot = el('div');
        foot.appendChild(el('span', 'qpt-shopcard-price', it.cents != null ? (it.store ? fmtStorePrice(it.cents) : C.fmtCAD(it.cents)) : ''));
        if (it.available === false) foot.appendChild(el('span', 'qpt-shopcard-badge out', 'Sold out'));
        a.appendChild(foot);
        row.appendChild(a);
      });
      mount.appendChild(row);
    }

    function fromCollection() {
      fetch(rootPath + 'collections/' + encodeURIComponent(collection) + '/products.json?limit=9')
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (data) { paint(fromRestFormat(data.products || [])); })
        .catch(function () { /* no slider — never break the page over suggestions */ });
    }

    if (storeProductId) {
      fetch(rootPath + 'recommendations/products.json?product_id=' + storeProductId + '&limit=8')
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (data) {
          var items = fromJsFormat(data.products || []);
          if (items.length >= 2) paint(items);
          else fromCollection();
        })
        .catch(fromCollection);
    } else {
      fromCollection();
    }
  }

  /* ---------- routing (market <-> product <-> index page) ---------- */

  function showMarket() {
    if (widget) { widget.destroy(); widget = null; }
    productEl.hidden = true;
    productEl.textContent = '';
    marketEl.hidden = false;
    root.setAttribute('data-view', 'market');
    // the tape can't arm while the market view is hidden (zero width) —
    // rebuild it now if the loop never went live
    var tapeEl = root.querySelector('[data-qpt-stats]');
    if (tapeEl && products.length && !tapeEl.classList.contains('qpt-tape--live')) renderStats();
    document.title = baseTitle;
  }

  function showProduct(id) {
    var p = byId[id];
    if (!p) { showMarket(); return; }
    marketEl.hidden = true;
    productEl.hidden = false;
    root.setAttribute('data-view', 'product');
    if (widget) { widget.destroy(); widget = null; }
    renderProductView(p);
  }

  function showIndexPage(key) {
    if (key === 'sets') {
      // the set-index directory page (no chart of its own)
      if (!lastIdx) { showMarket(); return; }
      if (widget) { widget.destroy(); widget = null; }
      marketEl.hidden = true;
      productEl.hidden = false;
      root.setAttribute('data-view', 'index');
      renderAllSetsPage(lastIdx);
      return;
    }
    var ix = resolveIndex(key);
    if (!ix) { showMarket(); return; }
    if (widget) { widget.destroy(); widget = null; }
    marketEl.hidden = true;
    productEl.hidden = false;
    root.setAttribute('data-view', 'index');
    renderIndexPage(ix);
  }

  function navigateToProduct(id) {
    var u = new URL(location.href);
    u.searchParams.set('product', id);
    u.searchParams.delete('index');
    history.pushState({ qpt: id }, '', u);
    showProduct(id);
  }

  function navigateToIndex(key) {
    var u = new URL(location.href);
    u.searchParams.set('index', key);
    u.searchParams.delete('product');
    history.pushState({ qidx: key }, '', u);
    showIndexPage(key);
  }

  root.addEventListener('click', function (ev) {
    var hero = ev.target.closest('a[data-set]');
    if (hero) {
      // hero banner: filter to its set in place (the href works without JS too)
      ev.preventDefault();
      applyEraSet(null, hero.getAttribute('data-set'), true);
      return;
    }
    var gi = ev.target.closest('[data-goidx]');
    if (gi) { navigateToIndex(gi.getAttribute('data-goidx')); return; }
    var btn = ev.target.closest('[data-open]');
    if (btn) navigateToProduct(btn.getAttribute('data-open'));
  });
  window.addEventListener('popstate', function (ev) {
    if (ev.state && ev.state.qpt) showProduct(ev.state.qpt);
    else if (ev.state && ev.state.qidx) showIndexPage(ev.state.qidx);
    else showMarket();
  });
  // Safari restores from back-forward cache with CSS animations dead —
  // rebuild the ticker tape so the loop restarts
  window.addEventListener('pageshow', function (ev) {
    if (ev.persisted && products.length && root.getAttribute('data-view') === 'market') renderStats();
  });

  /* ---------- market controls ---------- */

  searchEl.addEventListener('input', function () {
    query = searchEl.value.trim().toLowerCase();
    renderCatalog();
  });
  filtersEl.addEventListener('click', function (ev) {
    var b = ev.target.closest('[data-lang]');
    if (!b) return;
    lang = b.getAttribute('data-lang');
    filtersEl.querySelectorAll('[data-lang]').forEach(function (pill) {
      pill.setAttribute('aria-pressed', pill === b ? 'true' : 'false');
    });
    renderCatalog();
  });
  if (sortEl) {
    sortEl.addEventListener('change', function () {
      sort = sortEl.value;
      renderCatalog();
    });
  }
  if (typechipEl) {
    typechipEl.addEventListener('click', function () { applyType(null, false); });
  }
  if (mktEl) {
    mktEl.addEventListener('click', function (ev) {
      var b = ev.target.closest('[data-mkt]');
      if (b) setMode(b.getAttribute('data-mkt'));
    });
  }
  if (clearEl) {
    clearEl.addEventListener('click', resetFilters);
  }

  /* ---------- boot ---------- */

  function loadIndexes() {
    fetch(API + '/indexes?range=3m', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (idx) {
        lastIdx = idx;
        idxPayloadCache = { '3m': idx };
        renderIndexHero(idx);
        renderIndexList(idx);
        renderTypeCards(idx);
        renderIndexCards(idx);
        renderStats(); // ticker gains index + hot-set chips once data is here
        renderCatalog(); // set headers gain their "Set index" links once lastIdx exists
        // ?index= deep link resolves once index data is here (product links win)
        var u0 = new URL(location.href);
        var wantIndex = u0.searchParams.get('index');
        if (wantIndex && !u0.searchParams.get('product') && (wantIndex === 'sets' || resolveIndex(wantIndex))) {
          // rewrite this entry as the market base, then push the index page —
          // so Back returns to the market instead of leaving the site
          var clean = new URL(location.href);
          clean.searchParams.delete('index');
          history.replaceState({}, '', clean);
          navigateToIndex(wantIndex);
        }
      })
      .catch(function () {
        // index sections are additive — hide them rather than break the page
        [idxMainEl, idxListEl, idxCardsEl, typeCardsEl].forEach(function (m) {
          if (m) { var sec = m.closest('section'); (sec || m).hidden = true; }
        });
      });
  }

  function fail() {
    catalogEl.textContent = '';
    if (gainersEl) gainersEl.textContent = '';
    if (losersEl) losersEl.textContent = '';
    var s = el('div', 'qpt-status');
    s.appendChild(el('p', null, 'Couldn’t load live market data.'));
    var r = el('button', 'qpt-pill', 'Retry');
    r.type = 'button';
    r.addEventListener('click', boot);
    s.appendChild(r);
    catalogEl.appendChild(s);
  }

  function boot() {
    fetch(API + '/catalog', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (data) {
        products = data.products || [];
        byId = {};
        products.forEach(function (p) { byId[p.id] = p; });
        logoMap = data.set_logos || {};
        // Shopify Markets picks the storefront currency per visitor country;
        // mirror the iOS app: convert the CAD market data for display using
        // the worker's daily rates map (no rate for the code → stay CAD)
        var shopCur = (root.getAttribute('data-currency') || 'CAD').toUpperCase();
        C.setMoney(shopCur, (data.rates || {})[shopCur]);
        if (data.updated_at) {
          var d = new Date(data.updated_at).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' });
          updatedAtText = 'Last updated ' + d + '. ';
          updatedEl.textContent = 'Prices last updated ' + d + '.';
        }
        buildTaxonomy();
        var params = new URL(location.href).searchParams;
        if (params.get('market') === 'singles') {
          mode = 'singles';
          [idxMainEl, idxListEl, typeCardsEl, idxCardsEl].forEach(function (m) {
            if (m) { var sec = m.closest('section'); (sec || m).hidden = true; }
          });
        }
        var wantSet = params.get('set');
        var wantEra = params.get('era');
        var wantType = params.get('ptype');
        if (wantSet && !products.some(function (p) { return p.set_name === wantSet; })) wantSet = null;
        if (wantEra && !products.some(function (p) { return eraOf(p) === wantEra; })) wantEra = null;
        activeSet = wantSet;
        activeEra = wantEra;
        activeType = wantType && ptypeDef(wantType) ? wantType : null;
        if (activeSet && !activeEra) {
          var owner = products.filter(function (p) { return p.set_name === activeSet; })[0];
          if (owner) activeEra = eraOf(owner);
        }
        navSync();

        renderStats();
        renderLists();
        renderEraTabs();
        renderSetStrip();
        renderCatalog();
        loadIndexes();

        var url0 = new URL(location.href);
        var want = url0.searchParams.get('product');

        // ?handle= is the crawlable form of the same deep link: product pages
        // can link here server-side, without knowing the tracker's own ids.
        if (!want) {
          var wantHandle = url0.searchParams.get('handle');
          if (wantHandle) {
            for (var hi = 0; hi < products.length; hi++) {
              if (products[hi].shopify_handle === wantHandle) {
                want = String(products[hi].id);
                break;
              }
            }
          }
        }

        if (want && byId[want]) {
          // rewrite this entry as the market base, then push the product entry —
          // so Back returns to the market view instead of leaving the page
          var clean = new URL(location.href);
          clean.searchParams.delete('product');
          clean.searchParams.delete('handle');
          history.replaceState({}, '', clean);
          navigateToProduct(want);
        }
      })
      .catch(fail);
  }

  boot();
})();
